<script setup>
/*
    SpeedometerWidget.vue - Speedometer-style gauge widget

    This widget renders a speedometer-style gauge that displays a numeric value
    with a rotating needle on a circular dial. The dial spans from 7 o'clock to 5 o'clock
    (270 degrees) and includes major/minor tick marks, value labels, and a red zone.

    Components:
        primary: <canvas> - The main canvas element for rendering the speedometer

    Widget Properties:
        format - Number formatting options for the displayed value
        max - Maximum value for the gauge scale (default: 100)
        min - Minimum value for the gauge scale (default: 0)
        range - Animation range settings
        text - Label text to display below the value
        value - Current numeric value to display

    CSS:
        background (--v-theme-background-lighten-1, 1)
        --w-fill-color (v-theme-primary, 0.35)
        --w-text-color (v-theme-text)
        --w-needle-color (v-theme-text-darken-1)
        --w-units ()
        --w-max (widget.max)
        --w-min (widget.min)

    Animation:
        - Values are animated smoothly using a frame-based approach
        - Animation duration is controlled by the period and frame rate
        - Needle rotates from current position to target position over time
 */
import {reactive, ref} from 'vue'
import {clone, delay, waitRender} from '@/paks/vu-app'
import {formatValue} from '@/paks/js-format'
import {getColor, getProp, getWidgetProps} from '@/paks/vu-dash/BoardUtils'

// Expose the update function for external calls
defineExpose({update})

// Component props
const props = defineProps({
    widget: Object,
})

// Reactive state object containing all widget properties and display settings
const page = reactive({
    args: {}, // Widget arguments and styling
    animatedValue: 0, // Current animated value (used for smooth transitions)
    configuring: false, // Flag to prevent concurrent configuration
    data: {}, // Widget.value data
    frame: FRAME_RATE, // Animation frame rate
    height: null, // Canvas height
    min: null, // Minimum scale value
    max: null, // Maximum scale value
    period: props.widget.range?.period || PERIOD, // Animation period in seconds
    offsetHeight: 0, // Element offset height
    offsetWidth: 0, // Element offset width
    styles: {}, // CSS styles
    theme: {}, // Color theme
    text: props.widget.text || '', // Label text
    updating: null, // Animation state flag
    width: null, // Canvas width
    value: props.widget.value, // Current target value
    x: 0, // Canvas x position
    y: 0, // Canvas y position
})

// Component reference to the canvas element
const primary = ref(null)

// Animation constants
const PERIOD = 5 // Default animation period in seconds
const FRAME_RATE = 10 // Animation frames per second
const MIN_SIZE = 5 // Minimum widget size in pixels

/*
    Update the drawn value with smooth animation.
    
    This function handles the main animation loop that smoothly transitions
    the needle from its current position to the target value position.
    
    Parameters:
        params.initialize - Force reconfiguration of canvas dimensions
        
    Animation Process:
        1. Configure canvas if needed (size changes, initialization)
        2. Bound the new value within min/max limits
        3. Start animation loop with calculated steps
        4. Incrementally move animated value toward target
        5. Redraw speedometer on each frame
        6. Continue until target is reached or animation timeout
 */
async function update(params = {}) {
    let widget = clone(props.widget)
    let vel = primary.value

    // Reconfigure canvas if dimensions changed or initializing
    if (params.initialize || vel?.height == 0 || !page.width || !page.height) {
        configure()
    }
    // Get widget properties and styling
    Object.assign(page, getWidgetProps(widget, {primary}))
    page.theme = makeTheme(page.styles)

    // Bound the target value within the defined min/max range
    let value = (page.value = boundValue(page.data.primary || 0))
    // Exit early if canvas unavailable, already updating, invalid value, or page hidden
    if (!vel || page.updating || isNaN(value) || hidden()) {
        return
    }
    page.text = widget.text || ''

    // Start animation process
    page.updating = true

    // Calculate animation steps based on period and frame rate
    let steps = Math.min(page.period * page.frame, 10)

    // Handle edge cases for animated value
    if (page.animatedValue > page.max) {
        page.animatedValue = page.max
    } else if (!page.animatedValue) {
        steps = 1 // Jump immediately if starting from 0
    }

    // Calculate animation parameters
    let originalDiff = value - page.animatedValue
    let minDiff = Math.abs(originalDiff / 100) // Minimum change threshold
    let count = 25 // Maximum animation iterations

    // Animation loop - incrementally move needle toward target
    do {
        let diff = value - page.animatedValue
        let inc = diff / steps // Increment per step
        page.animatedValue += inc < 0 ? inc : inc

        // Redraw the speedometer with current animated value
        draw(value, page.animatedValue)

        // Check if we're close enough to target
        diff = value - page.animatedValue
        if (Math.abs(diff) <= minDiff) {
            break
        }

        // Wait for next frame
        await delay(250 / page.frame)
    } while (vel && value == page.value && count-- > 0)

    // Finalize animation
    page.animatedValue = value
    page.updating = false
    await waitRender()

    // Handle value changes during animation
    if (value != page.value) {
        update(page.value)
    }
}

/*
    Configure canvas dimensions and layout.
    
    This function calculates the optimal size and position for the speedometer
    based on the available widget space. It ensures the speedometer is square
    and properly centered within the widget bounds.
    
    Layout Calculations:
        - Creates a square canvas based on the smaller of width/height
        - Centers the speedometer within the available space
        - Adjusts for widget header if present
        - Clears the canvas for fresh drawing
 */
function configure() {
    let vel = primary.value
    if (!vel || page.configuring) {
        return
    }
    page.configuring = true

    let c = vel.getContext('2d')
    let widgetElement = primary.value
    let {offsetHeight, offsetWidth} = widgetElement

    // Calculate square dimensions (90% of available space)
    page.square = Math.max(Math.min(offsetHeight, offsetWidth), MIN_SIZE)
    page.square = parseInt(page.square * 0.9)
    page.height = page.width = page.square

    // Center the speedometer within the widget
    page.x = (offsetWidth - page.square) / 2
    page.y = (offsetHeight - page.square) / 2

    // Adjust position if widget has a header
    if (props.widget.header) {
        page.y += Math.max(10, parseInt(page.square / 40))
    }

    // Set canvas dimensions and clear
    c.canvas.width = offsetWidth
    c.canvas.height = offsetHeight
    c.clearRect(0, 0, vel.width, vel.height)
    page.configuring = false
}

function makeTheme() {
    let backgroundColor = getColor(page, 'background', '--v-theme-background-lighten-1', {opacity: 1})
    let fillColor = getColor(page, '--w-fill-color', '--v-theme-primary', {_opacity: 0.35})
    let textColor = getColor(page, '--w-text-color', '--v-theme-text')
    let needleColor = getColor(page, '--w-needle-color', '--v-theme-text-darken-1')
    let tickColor = getColor(page, '--w-tick-color', '--v-theme-text')
    let redZoneColor = getColor(page, '--w-red-zone-color', '--v-theme-error')
    let greenZoneColor = getColor(page, '--w-green-zone-color', '--v-theme-info', {opacity: 0.25})
    let dialColor = getColor(page, '--w-dial-color', '--v-theme-primary', {opacity: 0.35})
    let max = getProp(page, '--w-max', props.widget.max)
    let min = getProp(page, '--w-min', props.widget.min)
    let units = getProp(page, '--w-units', '')

    let theme = {
        background: backgroundColor,
        dialColor: dialColor,
        fillColor: fillColor,
        greenZoneColor: greenZoneColor,
        needleColor: needleColor,
        redZoneColor: redZoneColor,
        tickColor: tickColor,
        textColor: textColor,
        max: max,
        min: min,
        units: units,
    }
    return theme
}

/*
    Main drawing function that renders the complete speedometer.
    
    This function coordinates all drawing operations to create the speedometer:
    1. Sets up canvas context and calculates dimensions
    2. Applies theme colors (light/dark mode)
    3. Draws the dial background with tick marks and numbers
    4. Draws the central value display
    5. Draws the needle pointing to the current value
    
    Parameters:
        value - The target value to display
        animatedValue - The current animated position of the needle
 */
function draw(value, animatedValue) {
    let vel = primary.value
    if (!vel) {
        return
    }
    // Set up canvas context and dimensions
    let c = vel.getContext('2d')
    let styles = page.styles
    let height = parseInt(page.height)
    let width = parseInt(page.width)
    let radius = page.square * 0.4 // Speedometer radius
    let cx = page.x + width / 2 // Center X coordinate
    let cy = page.y + height / 2 // Center Y coordinate
    let font = styles.getPropertyValue('font-family') || 'Inter'
    // Scale font size based on widget size
    let fontSize = Math.round(12 + Math.max(height - 200, 0) * 0.04)
    let theme = page.theme

    // Ensure minimum radius
    if (radius <= 1) {
        radius = 1
    }

    // Clear canvas and set background
    c.save()
    c.fillStyle = theme.background
    c.clearRect(page.x, page.y, width, height)
    c.fillRect(page.x, page.y, width, height)

    // Draw speedometer components in order
    drawSpeedometerDial(c, cx, cy, radius, theme, font, fontSize) // Dial and scale
    drawCentralDisplay(c, cx, cy, radius, theme, font, fontSize, value) // Value display
    drawNeedle(c, cx, cy, radius, theme, animatedValue) // Needle

    c.restore()
}

/*
    Draw the speedometer dial with tick marks, numbers, and red zone.
    
    This function creates the circular dial that forms the background of the speedometer.
    The dial spans 270 degrees (from 7 o'clock to 5 o'clock) and includes:
    - Outer circular ring
    - Major tick marks at 10 intervals with value labels
    - Minor tick marks between major ticks (4 per interval)
    - Red zone in the last 20% of the scale
    
    Parameters:
        c - Canvas 2D context
        cx, cy - Center coordinates
        radius - Dial radius
        theme - Color theme object
        font - Font family
        fontSize - Font size
 */
function drawSpeedometerDial(c, cx, cy, radius, theme, font, fontSize) {
    // Draw outer ring of the dial
    c.beginPath()
    c.arc(cx, cy, radius, 0, 2 * Math.PI, false)
    c.strokeStyle = theme.dialColor
    c.lineWidth = 3
    c.stroke()

    // Set up dial parameters
    let startAngle = 0.75 * Math.PI // Start at 7 o'clock (225°)
    let endAngle = 2.25 * Math.PI // End at 5 o'clock (405° or 45°)
    let totalAngle = endAngle - startAngle // 270° total sweep
    let steps = 10 // Number of major divisions
    let stepAngle = totalAngle / steps // Angle between major ticks

    // Draw major tick marks and value labels
    for (let i = 0; i <= steps; i++) {
        let angle = startAngle + i * stepAngle
        let value = page.min + (i / steps) * (page.max - page.min) // Scale value at this position
        let tickRadius = radius * 0.85 // Inner radius for tick marks
        let numberRadius = radius * 0.7 // Radius for number placement

        // Draw major tick mark
        c.beginPath()
        c.moveTo(cx + Math.cos(angle) * tickRadius, cy + Math.sin(angle) * tickRadius)
        c.lineTo(cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius)
        c.strokeStyle = theme.tickColor
        c.lineWidth = 2
        c.stroke()

        // Draw value label
        c.font = `${fontSize}px ${font}`
        c.fillStyle = theme.textColor
        c.textAlign = 'center'
        c.textBaseline = 'middle'
        // let valueText = formatValue(Math.round(value), {format: props.widget.format})
        let valueText = Math.round(value).toString()
        if (valueText.length > 6) {
            valueText = valueText.slice(0, -6) + 'm'
        } else if (valueText.length > 3) {
            valueText = valueText.slice(0, -3) + 'k'
        }
        c.fillText(
            valueText,
            cx + Math.cos(angle) * numberRadius,
            cy + Math.sin(angle) * numberRadius
        )

        // Draw minor tick marks between major ticks
        if (i < steps) {
            for (let j = 1; j < 5; j++) {
                // 4 minor ticks per major interval
                let minorAngle = angle + (j * stepAngle) / 5
                let minorTickRadius = radius * 0.9

                c.beginPath()
                c.moveTo(cx + Math.cos(minorAngle) * minorTickRadius, cy + Math.sin(minorAngle) * minorTickRadius)
                c.lineTo(cx + Math.cos(minorAngle) * radius, cy + Math.sin(minorAngle) * radius)
                c.strokeStyle = theme.tickColor
                c.lineWidth = 1
                c.stroke()
            }
        }
    }

    // Draw red zone arc (danger zone for high values)
    // Red zone covers the last 20% of the dial (from 80% to 100%)
    let redZoneStart = startAngle + totalAngle * 0.8
    c.beginPath()
    c.arc(cx, cy, radius * 0.95, redZoneStart, endAngle, false)
    c.strokeStyle = theme.redZoneColor
    c.lineWidth = 8
    c.stroke()

    let greenZoneStart = startAngle
    endAngle = startAngle + totalAngle * 0.8
    c.beginPath()
    c.arc(cx, cy, radius * 0.95, greenZoneStart, endAngle, false)
    c.strokeStyle = theme.greenZoneColor
    c.lineWidth = 8
    c.stroke()
}

/*
    Draw the central value display and needle hub.
    
    This function renders the center area of the speedometer which includes:
    - Small central circle as the needle hub
    - Main numeric value positioned at the bottom of the dial
    - Optional text label below the value
    
    The value is displayed in large, bold text and positioned just inside
    the bottom of the dial for easy reading.
    
    Parameters:
        c - Canvas 2D context
        cx, cy - Center coordinates
        radius - Dial radius
        theme - Color theme object
        font - Font family
        fontSize - Base font size
        value - The numeric value to display
 */
function drawCentralDisplay(c, cx, cy, radius, theme, font, fontSize, value) {
    // Draw small central circle as needle hub background
    c.beginPath()
    c.arc(cx, cy, radius * 0.15, 0, 2 * Math.PI, false)
    c.fillStyle = theme.background
    c.fill()
    c.strokeStyle = theme.dialColor
    c.lineWidth = 2
    c.stroke()

    // Draw main numeric value
    c.font = `bold ${Math.round(fontSize * 2)}px ${font}`
    c.fillStyle = theme.textColor
    c.textAlign = 'center'
    c.textBaseline = 'middle'
    let valueText = formatValue(value, {format: props.widget.format})
    // Position main value at bottom inside the dial
    c.fillText(valueText, cx, cy + radius - fontSize * 1.5)

    // Draw optional text label below the main value
    if (page.text) {
        c.font = `${Math.round(fontSize * 0.8)}px ${font}`
        c.fillStyle = theme.textColor
        c.textAlign = 'center'
        c.textBaseline = 'middle'
        c.fillText(page.text, cx, cy + radius - fontSize + fontSize * 1.2)
    }
}

/*
    Draw the speedometer needle pointing to the current value.
    
    This function renders the needle that indicates the current value on the dial.
    The needle is drawn as a diamond/arrow shape and rotates to point to the
    correct position on the scale.
    
    Needle Calculation:
    - Converts the animated value to a ratio (0-1) within the min/max range
    - Maps this ratio to the dial's 270-degree sweep
    - Rotates the needle to point to the calculated angle
    
    Parameters:
        c - Canvas 2D context
        cx, cy - Center coordinates
        radius - Dial radius
        theme - Color theme object
        animatedValue - Current animated value position
 */
function drawNeedle(c, cx, cy, radius, theme, animatedValue) {
    // Calculate needle angle based on value position
    let startAngle = 0.75 * Math.PI // 7 o'clock position (135°)
    let totalAngle = 1.5 * Math.PI // 270° sweep
    let valueRatio = Math.min(Math.max((animatedValue - page.min) / (page.max - page.min), 0), 1)
    let needleAngle = startAngle + valueRatio * totalAngle
    let needleLength = radius * 0.8 // Needle length (80% of radius)

    // Set up transformation matrix for needle rotation
    c.save()
    c.translate(cx, cy) // Move origin to center
    c.rotate(needleAngle) // Rotate to target angle

    // Draw needle shape (diamond/arrow pointing right)
    c.beginPath()
    c.moveTo(needleLength, 0) // Tip of needle
    c.lineTo(0, -6) // Upper back corner
    c.lineTo(-15, 0) // Back center
    c.lineTo(0, 6) // Lower back corner
    c.closePath()
    c.fillStyle = theme.needleColor
    c.fill()

    // Draw needle center hub
    c.beginPath()
    c.arc(0, 0, 8, 0, 2 * Math.PI, false)
    c.fillStyle = theme.needleColor
    c.fill()

    // Restore transformation matrix
    c.restore()
}

function nextPowerOf10(n) {
    if (n <= 10) return 10
    const exponent = Math.ceil(Math.log10(n))
    return Math.pow(10, exponent)
}

/*
    Set the min/max values for the speedometer. If not supplied, set to next power of 10.
 */
function setMinMax(v) {
    let theme = page.theme
    if (!theme.max) {
        // Max not defined. Set to next power of 10
        if (v > page.max || page.max == null) {
            page.max = nextPowerOf10(v)
        }
    } else {
        page.max = theme.max
    }
    page.min = (theme.min ? theme.min : props.widget.min) || 0
}

/*
    Bound a value within the defined min/max range.
    
    This utility function ensures that input values stay within the
    speedometer's configured range. Values below the minimum are
    clamped to the minimum, and values above the maximum are clamped
    to the maximum.
    
    Parameters:
        v - The input value to bound
        
    Returns:
        The bounded value within [page.min, page.max]
 */
function boundValue(v) {
    let value = v - 0 // Convert to number
    setMinMax(value)
    if (value < page.min) {
        value = page.min
    }
    if (value > page.max) {
        value = page.max
    }
    return value
}

/*
    Check if the page/tab is currently hidden.
    
    This function detects if the browser tab or window is not visible
    to the user. When hidden, animations are paused to save resources.
    
    Returns:
        true if the page is hidden, false if visible
 */
function hidden() {
    return document.hidden || document.msHidden || document.webkitHidden || document.mozHidden
}
</script>

<!--
    Speedometer Widget Template
    
    The template contains a single canvas element that serves as the drawing surface
    for the entire speedometer. The canvas is bound to the 'primary' ref and receives
    dynamic arguments from the page.args object for styling and behavior.
    
    Fallback text is displayed if the browser doesn't support HTML5 Canvas.
-->
<template>
    <canvas class="speedometer-widget" ref="primary" v-bind="page.args">
        This text is displayed if your browser does not support HTML5 Canvas.
    </canvas>
</template>

<!--
    Speedometer Widget Styles
    
    The styles ensure the canvas fills the entire widget container.
    All visual rendering is handled programmatically via the canvas 2D context,
    so minimal CSS is needed.
-->
<style lang="scss">
.speedometer-widget {
    height: 100%; // Fill widget height
    width: 100%; // Fill widget width
}
</style>
